import React, { memo, forwardRef } from 'react'
import { COLORS } from 'consts/colors'
import { TColorKey } from 'types'
import { getSizeWithPixels } from './utils'
import { TIconSize } from './types'

interface IProps {
  icon: React.ReactElement<React.SVGProps<SVGSVGElement>>
  size?: TIconSize
  color?: TColorKey
  customSize?: number
  className?: string
}

/*
 * IMPORTANT for new SVG icons:
 * To allow dynamic color control via the `color` prop, all **color-fillable paths** inside the SVG
 * (i.e., the parts that should inherit the icon color) must use `fill="currentColor"`.
 *
 * - `currentColor` is a special CSS keyword that makes the SVG path inherit the `color` property
 *   from its parent — just like regular text.
 * - This allows us to control SVG icon color dynamically via React props, CSS, or styled-components.
 * - It's especially useful for theming, hover states, and consistent styling.
 *
 * Exceptions:
 * - It's totally fine to use `fill="none"` or hardcoded colors (e.g., `fill="#FFFFFF"`, `fill="#FF6600"`, etc.)
 *   on paths that are meant to stay transparent or use fixed branding/styling colors.
 *
 * ✅ Correct usage in SVG files:
 *    <path d="..." fill="currentColor" />     ← for parts that should be styled via props
 *    <path d="..." fill="none" />             ← for transparent parts
 *    <path d="..." fill="#FF6600" />          ← for fixed-color parts
 */

const Icon = forwardRef<SVGSVGElement, IProps>(
  (
    {
      icon,
      size = 'small',
      color = 'secondaryLight',
      customSize,
      className,
      ...rest
    },
    ref
  ) => {
    const sizeWithPixels = customSize || getSizeWithPixels(size)

    return React.cloneElement(icon, {
      ref,
      className,
      width: sizeWithPixels,
      height: sizeWithPixels,
      style: { color: COLORS[color] },
      ...rest
    })
  }
)

Icon.displayName = 'Icon'

export default memo(Icon)
